const DB_NAME = 'installer_photo_buffer_v1';
const STORE = 'photos';
const DB_VERSION = 1;

const listeners = new Set();

const openDb = () =>
    new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB недоступен'));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                const store = db.createObjectStore(STORE, {
                    keyPath: 'id'
                });
                store.createIndex('taskId', 'taskId');
                store.createIndex('createdAt', 'createdAt');
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });

const withStore = async (mode, fn) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        let result;
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error || new Error('IDB'));
        try {
            const maybe = fn(store);
            if (maybe && typeof maybe.then === 'function') {
                maybe
                    .then(value => {
                        result = value;
                    })
                    .catch(reject);
            } else {
                result = maybe;
            }
        } catch (error) {
            reject(error);
        }
    });
};

const uid = () =>
    `p${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const fileToBase64 = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || '');
            const base64 = result.includes(',')
                ? result.split(',')[1]
                : result;
            resolve(base64);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

export const bufferPhotoSrc = photo =>
    `data:${photo.mime || 'image/jpeg'};base64,${photo.base64}`;

export const bufferPhotoToFile = photo => {
    const binary = atob(photo.base64 || '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new File(
        [bytes],
        photo.fileName || 'photo.jpg',
        { type: photo.mime || 'image/jpeg' }
    );
};

const notify = async () => {
    const photos = await listBufferedPhotos();
    listeners.forEach(fn => {
        try {
            fn(photos);
        } catch (error) {
            console.warn('photo buffer listener', error);
        }
    });
};

export const subscribePhotoBuffer = fn => {
    listeners.add(fn);
    listBufferedPhotos().then(fn).catch(() => fn([]));
    return () => listeners.delete(fn);
};

export const listBufferedPhotos = async (taskId = null) => {
    if (typeof indexedDB === 'undefined') return [];
    const rows = await withStore('readonly', store =>
        new Promise((resolve, reject) => {
            const req = store.getAll();
            req.onsuccess = () => resolve(req.result || []);
            req.onerror = () => reject(req.error);
        })
    );
    const list = (rows || []).sort(
        (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
    );
    if (taskId == null) return list;
    return list.filter(
        row => Number(row.taskId) === Number(taskId)
    );
};

export const addBufferedPhoto = async ({ taskId, file }) => {
    if (!taskId || !file) {
        throw new Error('Нет заявки или файла');
    }
    const base64 = await fileToBase64(file);
    const row = {
        id: uid(),
        taskId: Number(taskId),
        fileName: file.name || 'photo.jpg',
        mime: file.type || 'image/jpeg',
        size: file.size || null,
        base64,
        createdAt: Date.now()
    };
    await withStore('readwrite', store => {
        store.put(row);
    });
    await notify();
    return row;
};

export const removeBufferedPhoto = async id => {
    await withStore('readwrite', store => {
        store.delete(id);
    });
    await notify();
};

export const clearBufferedPhotos = async taskId => {
    const rows = await listBufferedPhotos(taskId);
    await withStore('readwrite', store => {
        rows.forEach(row => store.delete(row.id));
    });
    await notify();
};
